import React, { useState, useEffect } from 'react';
import type { Team } from '../../../../types';
import { teamService } from '../../services/teamService';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';

interface TeamSwitcherProps {
  currentTeam: Team | null;
  onTeamSwitch: (team: Team) => void;
}

export const TeamSwitcher: React.FC<TeamSwitcherProps> = ({ currentTeam, onTeamSwitch }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [forceUpdate, setForceUpdate] = useState(0);

  // 加载用户的所有团队
  const loadTeams = async () => {
    setLoading(true);
    try {
      const userTeams = await teamService.getMyTeams();
      setTeams(userTeams);

      // 立即返回最新数据，以便调用方获取
      return userTeams;
    } catch (error) {
      console.error('❌ 加载团队列表失败:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeams();
  }, []);

  // 切换团队
  const handleSwitchTeam = async (team: Team) => {
    if (currentTeam && team.id === currentTeam.id) return;
    
    setSwitching(team.id);
    try {
      await teamService.switchTeam(team.id);
      onTeamSwitch(team);
    } catch (error) {
      console.error('切换团队失败:', error);
    } finally {
      setSwitching(null);
    }
  };

  // 加入团队
  const handleJoinTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;

    setLoading(true);
    try {
      const newTeam = await teamService.joinTeam(inviteCode.trim().toUpperCase());

      // 刷新用户信息以获取最新的团队成员关系
      try {
        const { useAuthStore } = await import('../../../../stores/authStore');
        const { authService } = await import('../../../auth/services/authService');
        const userInfo = await authService.getCurrentUser();
        useAuthStore.getState().updateUser(userInfo);
        console.log('✅ 用户信息已更新');
      } catch (error) {
        console.warn('刷新用户信息失败:', error);
      }

      await loadTeams(); // 重新加载团队列表
      setInviteCode('');
      setShowJoinForm(false);
      console.log('成功加入团队:', newTeam.name);
    } catch (error) {
      console.error('加入团队失败:', error);
      // TODO: 显示错误提示
    } finally {
      setLoading(false);
    }
  };

  // 创建团队
  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamName.trim()) return;

    setLoading(true);
    try {
      const newTeam = await teamService.createTeam(teamName.trim(), teamDescription.trim() || undefined);

      // 刷新用户信息以获取最新的团队成员关系
      try {
        const { useAuthStore } = await import('../../../../stores/authStore');
        const { authService } = await import('../../../auth/services/authService');
        const userInfo = await authService.getCurrentUser();
        useAuthStore.getState().updateUser(userInfo);
      } catch (error) {
        console.warn('刷新用户信息失败:', error);
      }

      // 重新加载团队列表
      await loadTeams();

      // 强制重新渲染以确保UI更新
      setForceUpdate(prev => prev + 1);

      setTeamName('');
      setTeamDescription('');
      setShowCreateForm(false);
    } catch (error) {
      console.error('创建团队失败:', error);
      // TODO: 显示错误提示
    } finally {
      setLoading(false);
    }
  };

  if (loading && teams.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <svg className="animate-spin w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="ml-2 text-gray-600">加载团队列表...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 团队列表 */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">我的团队</h3>
        <div className="space-y-2">
          {teams.map(team => {
            const isCurrent = currentTeam ? team.id === currentTeam.id : false;
            const isSwitching = switching === team.id;

            return (
              <div 
                key={team.id}
                className={`flex items-center justify-between p-3 rounded-lg border transition-colors duration-200 ${
                  isCurrent 
                    ? 'border-blue-200 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-medium mr-3 ${
                    isCurrent ? 'bg-blue-500' : 'bg-gray-400'
                  }`}>
                    {team.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{team.name}</div>
                    {team.description && (
                      <div className="text-xs text-gray-500">{team.description}</div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center">
                  {isCurrent ? (
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                      当前团队
                    </span>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleSwitchTeam(team)}
                      loading={isSwitching}
                      disabled={isSwitching}
                      className="text-xs"
                    >
                      切换
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 加入/创建团队 */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">加入或创建团队</h3>
        
        <div className="space-y-3">
          {/* 加入团队按钮/表单 */}
          {!showJoinForm ? (
            <button
              onClick={() => setShowJoinForm(true)}
              className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-300 hover:text-blue-600 transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              使用邀请码加入团队
            </button>
          ) : (
            <form onSubmit={handleJoinTeam} className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
              <Input
                label="邀请码"
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="请输入8位邀请码"
                maxLength={8}
                className="text-center font-mono text-lg tracking-wider"
                autoFocus
              />

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShowJoinForm(false);
                    setInviteCode('');
                  }}
                  className="flex-1"
                >
                  取消
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={!inviteCode.trim() || loading}
                  loading={loading}
                  className="flex-1"
                >
                  加入团队
                </Button>
              </div>
            </form>
          )}

          {/* 创建团队按钮/表单 */}
          {!showCreateForm ? (
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-green-300 hover:text-green-600 transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              创建新团队
            </button>
          ) : (
            <form onSubmit={handleCreateTeam} className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm space-y-4">
              <Input
                label="团队名称"
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="请输入团队名称"
                maxLength={100}
                autoFocus
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  团队描述 (可选)
                </label>
                <textarea
                  value={teamDescription}
                  onChange={(e) => setTeamDescription(e.target.value)}
                  placeholder="简单描述一下团队..."
                  maxLength={500}
                  rows={3}
                  className="w-full px-4 py-2.5 border-0 bg-gray-50 rounded-lg focus:outline-none focus:bg-white focus:ring-1 focus:ring-blue-400 transition-all duration-200 resize-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShowCreateForm(false);
                    setTeamName('');
                    setTeamDescription('');
                  }}
                  className="flex-1"
                >
                  取消
                </Button>
                <Button
                  type="submit"
                  variant="success"
                  size="sm"
                  disabled={!teamName.trim() || loading}
                  loading={loading}
                  className="flex-1"
                >
                  创建团队
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};